import React from 'react';
import { NavLink } from 'react-router-dom';
import { Calendar, Bell, Users, MessageCircle, Trophy, Settings, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/use-language';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@/hooks/use-auth';
import { useMessage } from '@/hooks/use-message';
import { withAuthRoutePrefix } from '@/config/auth';
import { useLocation } from 'react-router-dom';

export function BottomNavBar() {
  const { t } = useLanguage();
  const { unreadCount } = useNotifications();
  const { user } = useAuth();
  const { conversations } = useMessage();
  const location = useLocation();
  const totalUnreadMessages = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
  
  const navItems = [
    {
      to: '/',
      icon: Calendar,
      labelKey: 'nav.events',
      hasNotification: false
    },
    {
      to: '/notifications',
      icon: Bell,
      labelKey: 'nav.notifications',
      hasNotification: unreadCount > 0,
      notificationCount: unreadCount
    },
    {
      to: '/messages',
      icon: MessageCircle,
      labelKey: 'nav.messages',
      hasNotification: totalUnreadMessages > 0,
      notificationCount: totalUnreadMessages
    },
    {
      to: '/profile',
      icon: Settings,
      labelKey: 'nav.profile',
      hasNotification: false
    }
  ];

  if (user?.isAdmin) {
    navItems.splice(4, 0, {
      to: '/admin',
      icon: Shield,
      labelKey: 'nav.admin',
      hasNotification: false
    });
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-bottom-nav-bg border-t border-bottom-nav-border shadow-bottom-nav-shadow z-[99] max-w-full" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="flex items-center justify-around h-16 px-1 sm:px-2 max-w-full">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={withAuthRoutePrefix(item.to, location.pathname)}
            state={item.to === '/groups' ? { viewType: 'main' } : undefined}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center justify-center min-w-0 flex-1 py-1 px-1 relative",
                isActive
                  ? "text-bottom-nav-active"
                  : "text-bottom-nav-inactive hover:text-bottom-nav-active"
              )
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <item.icon className="h-5 w-5 mb-1" />
                  {item.hasNotification && (
                    <div className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-notification-badge rounded-full flex items-center justify-center">
                      {(item as any).notificationCount && (item as any).notificationCount > 0 && (
                        <span className="text-xs text-white font-medium px-1">
                          {(item as any).notificationCount > 9 ? '9+' : (item as any).notificationCount}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-xs font-medium truncate max-w-full">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
